import React, { useEffect, useState } from 'react';

import { Icon } from 'design-system-web';
import { Button, Flex, Input } from 'components';
import { useTranslation } from 'react-i18next';
import { H4, P } from 'components/typography';
import {
  calculateTotalMinutes,
} from 'date-time-manipulations';

import styles from './TaskTime.scss';
import { useChannelServiceRequestFeaturesContext } from 'lane-shared/hooks';

const MAX_INT32_VALUE = 2 ** 31 - 1;
const MAX_DIGITS = 10;

interface TaskTimeFormProps {
  initialHours: number | null;
  initialMinutes: number | null;
  onEntityUpdated: (fields: { hours: number; minutes: number }) => void;
  editHours: boolean;
  setEditHours: (val: boolean) => void;
}

export function TaskTimeForm({
  initialHours,
  initialMinutes,
  onEntityUpdated,
  editHours,
  setEditHours,
}: TaskTimeFormProps) {
  const { t } = useTranslation();
  const { serviceRequestFeatures } = useChannelServiceRequestFeaturesContext();

  const normalizedInitialHours = initialHours === 0 ? null : initialHours;
  const normalizedInitialMinutes = initialMinutes === 0 ? null : initialMinutes;

  const [hours, setHours] = useState<number | null>(normalizedInitialHours);
  const [minutes, setMinutes] = useState<number | null>(normalizedInitialMinutes);
  const [hoursInput, setHoursInput] = useState<string>(normalizedInitialHours?.toString() ?? '');
  const [minutesInput, setMinutesInput] = useState<string>(normalizedInitialMinutes?.toString() ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const normalizedHours = initialHours === 0 ? null : initialHours;
    const normalizedMinutes = initialMinutes === 0 ? null : initialMinutes;
    
    setHours(normalizedHours);
    setMinutes(normalizedMinutes);
    setHoursInput(normalizedHours?.toString() ?? '');
    setMinutesInput(normalizedMinutes?.toString() ?? '');
    setValidationError(null);
  }, [initialHours, initialMinutes]);

  const isPositiveInteger = (value: string) => /^\d+$/.test(value) && Number(value) >= 0;

  const totalMinutes = calculateTotalMinutes(hours, minutes);
  const exceedsMaxValue = totalMinutes > MAX_INT32_VALUE;

  const isFormValid =
    ((hoursInput === '' || isPositiveInteger(hoursInput)) &&
     (minutesInput === '' || isPositiveInteger(minutesInput)) &&
     !(hoursInput === '' && minutesInput === '') &&
     !exceedsMaxValue);

  const handleHoursChange = (value: string) => {
    if (value.length > MAX_DIGITS) {
      return;
    }

    setHoursInput(value);

    if (isPositiveInteger(value)) {
      setHours(Number(value));
    } else if (value === '') {
      setHours(null);
    }
  };

  const handleMinutesChange = (value: string) => {
    if (value.length > MAX_DIGITS) {
      return;
    }

    setMinutesInput(value);

    if (isPositiveInteger(value)) {
      setMinutes(Number(value));
    } else if (value === '') {
      setMinutes(null);
    }
  };

  useEffect(() => {
    if (exceedsMaxValue) {
      setValidationError('shared.admin.workOrder.serviceRequest.taskTime.error.exceedsMaxValue');
    } else {
      setValidationError(null);
    }
  }, [exceedsMaxValue]);

  const handleSave = () => {
    if (isFormValid) {
      onEntityUpdated({
        hours: Number(hoursInput),
        minutes: Number(minutesInput),
      });
      setEditHours(false);
    }
  };

  const handleCancel = () => {
    setEditHours(false);
    const normalizedHours = initialHours === 0 ? null : initialHours;
    const normalizedMinutes = initialMinutes === 0 ? null : initialMinutes;
    
    setHours(normalizedHours);
    setMinutes(normalizedMinutes);
    setHoursInput(normalizedHours?.toString() ?? '');
    setMinutesInput(normalizedMinutes?.toString() ?? '');
    setValidationError(null);
  };

  const hasHours = hoursInput !== '' && hours !== null && hours > 0;
  const hasMinutes = minutesInput !== '' && minutes !== null;
  const shouldShowMinutes = hasMinutes && !(minutes === 0 && hasHours);

  return (
    <div className={styles.main}>
      {serviceRequestFeatures.updateServiceRequest && (
        <div className={styles.taskTimeContainer}>
          <Flex>
            <H4>{t('web.admin.workOrders.notesAndHours.taskTime')}</H4>
            {!editHours && (
              <Button
                startIcon={<Icon name="pencil" />}
                size="small"
                className={styles.EditButton}
                onClick={() => setEditHours(true)}
              >
                {t`web.admin.equipment.edit`}
              </Button>
            )}
          </Flex>

          {(hasHours || hasMinutes) && !editHours && (
            <Flex gap={1} m={[5, 0, 0, 0]}>
              {hasHours && (
                <P>
                  {t('web.admin.workOrders.notesAndHours.taskTime.hours-count', {
                    count: hours,
                  })}
                </P>
              )}
              {shouldShowMinutes && (
                <P>
                  {t('web.admin.workOrders.notesAndHours.taskTime.minutes-count', {
                    count: minutes,
                  })}
                </P>
              )}
            </Flex>
          )}

          {editHours && (
            <Flex
              direction="column"
              className={styles.taskTimeInputContainer}
              gap={4}
              m={[5, 0, 0, 0]}
            >
              <Input
                type="number"
                onChange={handleHoursChange}
                value={hoursInput}
                rightText={t('web.admin.workOrders.notesAndHours.taskTime.field.hours.unit')}
                label={t('web.admin.workOrders.notesAndHours.taskTime.field.hours')}
                fixedLabel
                input={{ min: 0, step: 1 }}
                testId="hoursField"
              />
              <Input
                type="number"
                onChange={handleMinutesChange}
                value={minutesInput}
                rightText={t('web.admin.workOrders.notesAndHours.taskTime.field.minutes.unit')}
                label={t('web.admin.workOrders.notesAndHours.taskTime.field.minutes')}
                fixedLabel
                input={{ min: 0, step: 1 }}
                testId="minutesField"
              />

              {validationError && (
                <div className={styles.validationError}>
                  {t(validationError)}
                </div>
              )}

              <Flex gap={3}>
                <Button
                  variant="activate-contained"
                  testId="saveHours"
                  onClick={handleSave}
                  disabled={!isFormValid}
                >
                  {t`web.admin.workOrders.notesAndHours.taskTime.button.save`}
                </Button>
                <Button
                  testId="cancelHours"
                  onClick={handleCancel}
                >
                  {t`web.admin.workOrders.notesAndHours.taskTime.button.cancel`}
                </Button>
              </Flex>
            </Flex>
          )}
        </div>
      )}
    </div>
  );
}
